import ApolloClientHexaService from 'ApolloClientHexaService';
import { clearShared, sharedRequest } from 'utils/sharedRequest';
import {
  ADD_COMMENT,
  CREATE_TICKET,
  CREATE_WORKFLOW,
  DAMAGE_DECLARATION_REPORT,
  GET_BULK_CANDIDATES,
  GET_TICKET,
  GET_TICKET_COUNTS,
  GET_TICKETS,
  GET_TICKETS_PAGE,
  GET_WORKFLOW,
  GET_WORKFLOW_LIST,
  GET_WORKFLOW_DASHBOARD,
  GET_WORKFLOW_DASHBOARD_TICKETS,
  GET_WORKFLOWS,
  REASSIGN_TICKET,
  RESOLVE_VIOLATION,
  TRANSITION_TICKET,
  BULK_APPROVE_TICKETS,
  UPDATE_WORKFLOW,
} from 'gql/workflow';

// ==============================|| WORKFLOWS ||============================== //

export const getWorkflows = async (activeOnly = false) => {
  try {
    const { data } = await ApolloClientHexaService.query({
      query: GET_WORKFLOWS,
      variables: { activeOnly },
      fetchPolicy: 'no-cache',
    });
    return { success: true, error: false, data: data?.getWorkflows || [] };
  } catch (error) {
    return { success: false, error: { ...error }, data: [] };
  }
};

// Workflow names for menus and pickers — shared between screens and cleared
// when a workflow is created or edited.
export const getWorkflowList = (activeOnly = false) =>
  sharedRequest(`workflows:list:${activeOnly}`, async () => {
    try {
      const { data } = await ApolloClientHexaService.query({
        query: GET_WORKFLOW_LIST,
        variables: { activeOnly },
        fetchPolicy: 'no-cache',
      });
      return { success: true, error: false, data: data?.getWorkflows || [] };
    } catch (error) {
      return { success: false, error: { ...error }, data: [] };
    }
  });

export const getWorkflow = async (workflowId) => {
  try {
    const { data } = await ApolloClientHexaService.query({
      query: GET_WORKFLOW,
      variables: { workflowId },
      fetchPolicy: 'no-cache',
    });
    return { success: true, error: false, data: data?.getWorkflow || null };
  } catch (error) {
    return { success: false, error: { ...error }, data: null };
  }
};

export const getWorkflowDashboardTickets = async ({
  workflowId,
  groupBy,
  groupKey,
  status,
  store,
  priority,
  country,
  from,
  to,
  page = 0,
  limit = 25,
}) => {
  try {
    const { data } = await ApolloClientHexaService.query({
      query: GET_WORKFLOW_DASHBOARD_TICKETS,
      variables: {
        workflowId: workflowId || null,
        groupBy,
        groupKey: groupKey || '',
        status: status || null,
        store: store || null,
        priority: priority || null,
        country: country || null,
        from: from || null,
        to: to || null,
        page,
        limit,
      },
      fetchPolicy: 'no-cache',
    });
    return { success: true, error: false, data: data?.getWorkflowDashboardTickets || null };
  } catch (error) {
    return { success: false, error: { ...error }, data: null };
  }
};

export const getWorkflowDashboard = async (
  workflowId,
  { trendDays = 30, store, priority, country, from, to } = {},
) => {
  try {
    const { data } = await ApolloClientHexaService.query({
      query: GET_WORKFLOW_DASHBOARD,
      variables: {
        workflowId: workflowId || null,
        trendDays,
        store: store || null,
        priority: priority || null,
        country: country || null,
        from: from || null,
        to: to || null,
      },
      fetchPolicy: 'no-cache',
    });
    return {
      success: true,
      error: false,
      data: data?.getWorkflowDashboard || null,
    };
  } catch (error) {
    return { success: false, error: { ...error }, data: null };
  }
};

export const createWorkflow = async (input) => {
  try {
    const { data } = await ApolloClientHexaService.mutate({
      mutation: CREATE_WORKFLOW,
      variables: { input },
    });
    clearShared('workflows:');
    return { success: true, error: false, data: data?.createWorkflow };
  } catch (error) {
    return { success: false, error: { ...error }, data: null };
  }
};

export const updateWorkflow = async (input) => {
  try {
    const { data } = await ApolloClientHexaService.mutate({
      mutation: UPDATE_WORKFLOW,
      variables: { input },
    });
    clearShared('workflows:');
    return { success: true, error: false, data: data?.updateWorkflow };
  } catch (error) {
    return { success: false, error: { ...error }, data: null };
  }
};

// ==============================|| TICKETS ||============================== //

export const getTickets = async (filter = {}) => {
  try {
    const { data } = await ApolloClientHexaService.query({
      query: GET_TICKETS,
      variables: { filter },
      fetchPolicy: 'no-cache',
    });
    return { success: true, error: false, data: data?.getTickets || [] };
  } catch (error) {
    return { success: false, error: { ...error }, data: [] };
  }
};

// Server-side paginated tickets → { items, pageInfo, counts }.
export const getTicketsPage = async (filter = {}) => {
  try {
    const { data } = await ApolloClientHexaService.query({
      query: GET_TICKETS_PAGE,
      variables: { filter },
      fetchPolicy: 'no-cache',
    });
    return { success: true, error: false, data: data?.getTicketsPage || null };
  } catch (error) {
    return { success: false, error: { ...error }, data: null };
  }
};

// Match counts for several list filters at once → [{ total, ids }], in order.
export const getTicketCounts = async (filters = []) => {
  try {
    const { data } = await ApolloClientHexaService.query({
      query: GET_TICKET_COUNTS,
      variables: { filters },
      fetchPolicy: 'no-cache',
    });
    return { success: true, error: false, data: data?.getTicketCounts || [] };
  } catch (error) {
    return { success: false, error: { ...error }, data: [] };
  }
};

// Tickets the viewer could approve in bulk (list fields + pinned steps).
export const getBulkCandidates = async (filter = {}) => {
  try {
    const { data } = await ApolloClientHexaService.query({
      query: GET_BULK_CANDIDATES,
      variables: { filter },
      fetchPolicy: 'no-cache',
    });
    return { success: true, error: false, data: data?.getTicketsPage?.items || [] };
  } catch (error) {
    return { success: false, error: { ...error }, data: [] };
  }
};

export const createTicket = async (input) => {
  try {
    const { data } = await ApolloClientHexaService.mutate({
      mutation: CREATE_TICKET,
      variables: { input },
    });
    return { success: true, error: false, data: data?.createTicket };
  } catch (error) {
    return { success: false, error: { ...error }, data: null };
  }
};

export const getTicket = async (ticketId) => {
  try {
    const { data } = await ApolloClientHexaService.query({
      query: GET_TICKET,
      variables: { ticketId },
      fetchPolicy: 'no-cache',
    });
    return { success: true, error: false, data: data?.getTicket || null };
  } catch (error) {
    return { success: false, error: { ...error }, data: null };
  }
};

export const transitionTicket = async (input) => {
  try {
    const { data } = await ApolloClientHexaService.mutate({
      mutation: TRANSITION_TICKET,
      variables: { input },
    });
    return { success: true, error: false, data: data?.transitionTicket };
  } catch (error) {
    return { success: false, error: { ...error }, data: null };
  }
};

// Approve several tickets at once (stages with bulk approval on). Each ticket
// gets the same checks as approving it alone; results are per ticket.
export const bulkApproveTickets = async (input) => {
  try {
    const { data } = await ApolloClientHexaService.mutate({
      mutation: BULK_APPROVE_TICKETS,
      variables: { input },
    });
    return { success: true, error: false, data: data?.bulkApproveTickets };
  } catch (error) {
    return { success: false, error: { ...error }, data: null };
  }
};

export const reassignTicket = async (input) => {
  try {
    const { data } = await ApolloClientHexaService.mutate({
      mutation: REASSIGN_TICKET,
      variables: { input },
    });
    return { success: true, error: false, data: data?.reassignTicket };
  } catch (error) {
    return { success: false, error: { ...error }, data: null };
  }
};

export const addComment = async (input) => {
  try {
    const { data } = await ApolloClientHexaService.mutate({
      mutation: ADD_COMMENT,
      variables: { input },
    });
    return { success: true, error: false, data: data?.addComment };
  } catch (error) {
    return { success: false, error: { ...error }, data: null };
  }
};

export const resolveViolation = async (ticketId) => {
  try {
    const { data } = await ApolloClientHexaService.mutate({
      mutation: RESOLVE_VIOLATION,
      variables: { ticketId },
    });
    return { success: true, error: false, data: data?.resolveViolation };
  } catch (error) {
    return { success: false, error: { ...error }, data: null };
  }
};

// Damage Declaration export for a date range (YYYY-MM-DD, inclusive) and an
// optional store → { fileName, mimeType, contentBase64, rowCount }.
export const getDamageDeclarationReport = async (input) => {
  try {
    const { data } = await ApolloClientHexaService.query({
      query: DAMAGE_DECLARATION_REPORT,
      variables: { input },
      fetchPolicy: 'no-cache',
    });
    return {
      success: true,
      error: false,
      data: data?.damageDeclarationReport || null,
    };
  } catch (error) {
    return { success: false, error: { ...error }, data: null };
  }
};
